/**
 * Nav model for the supervisor/admin time surfaces. Kept as data (not JSX) so
 * both the {@link import('./routes').TimeRoutes} table and the TenantShell tab
 * strip stay in sync from one source.
 */
export interface TimeNavItem {
  /** Route path and Fluent tab value. */
  value: string;
  /** Tab label. */
  label: string;
  /**
   * When true, only an Administrator sees this tab. The API is still the
   * authoritative gate (Admin-only endpoints 403 regardless); this only decides
   * rendering.
   */
  adminOnly?: boolean;
  /** When true, a View-Only user sees this tab (their read-only surface). */
  viewer?: boolean;
}

/**
 * Ordered tabs. `Admin` is the Administrator-only hub — Employees, Salary,
 * NetSuite, Kiosks, Departments and Diagnostics all live inside it as sub-sections
 * (see {@link AdminTab}). The first entry is the landing surface and the only one a
 * View-Only user sees.
 *
 * Employees and Salary used to be top-level tabs an Approver could see. They moved
 * INTO the Admin hub, which makes them Administrator-only — a deliberate narrowing
 * (Michael, 2026-09-02): an Approver no longer reaches the roster or Set/Reset PIN
 * and is left with Hours & Timesheet.
 */
export const TIME_NAV: readonly TimeNavItem[] = [
  { value: '/time', label: 'Hours & Timesheet', viewer: true },
  { value: '/admin', label: 'Admin', adminOnly: true },
];

/**
 * The tabs visible to the current user, by role:
 *   - View Only  → only the tabs flagged `viewer` (Hours & Timesheet);
 *   - Approver   → every non-admin tab;
 *   - Administrator → all tabs.
 * The API enforces the finer role/RLS boundary server-side.
 */
export function visibleNav(isAdmin: boolean, isViewer: boolean): readonly TimeNavItem[] {
  if (isViewer) {
    return TIME_NAV.filter((item) => item.viewer === true);
  }
  return TIME_NAV.filter((item) => item.adminOnly !== true || isAdmin);
}

/** The default surface a signed-in manager lands on. */
export const TIME_HOME = '/time';

/**
 * Resolve the active tab value for a given pathname. Falls back to the home tab
 * so the strip always has a valid selection (e.g. at `/`).
 */
export function activeNavValue(pathname: string): string {
  const match = TIME_NAV.find(
    (item) => pathname === item.value || pathname.startsWith(`${item.value}/`)
  );
  return match?.value ?? TIME_HOME;
}
